<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useApiStore } from "@/stores/api";
import { EXCEL_DIMENSIONALITY, type ExcelFunction } from "@/stores/types/api.types";
import { setFormAsTable } from "@/excel/excelOperations";

const apiStore = useApiStore();
const { apiEntries } = storeToRefs(apiStore);

const registrableFunctions = computed(() => {
  const funcs: ExcelFunction[] = [];
  apiEntries.value.forEach((entry) => funcs.push(...entry.registrableFunctions));
  return funcs;
});

const selectedFunction = ref<ExcelFunction>();

const draftFormGenerator = (func: ExcelFunction) => {
  return func?.parameters.map((param) => ({
    [param.name]:
      param.dimensionality !== EXCEL_DIMENSIONALITY.matrix &&
      !param.nestLevel &&
      (typeof param.default === "string" || typeof param.default === "number" || typeof param.default === "boolean")
        ? param.default
        : "",
  }));
};

watch(registrableFunctions, (newFunctions) => {
  if (selectedFunction.value && !newFunctions.includes(selectedFunction.value)) {
    selectedFunction.value = undefined;
  }
});

watch(selectedFunction, async (newFunction) => {
  if (newFunction) {
    const responseItem = draftFormGenerator(newFunction);
    await setFormAsTable(responseItem, newFunction);
  }
});
</script>

<template>
  <v-card elevation="0" flat>
    <v-card-text>
      <v-combobox
        v-model="selectedFunction"
        :items="registrableFunctions"
        item-title="name"
        item-value="url"
        label="API Function"
        hint="Choose a function to generate the form"
        color="primary"
        persistent-hint
        chips
        clearable
      >
        <template v-slot:chip="{ props, item }">
          <v-chip v-bind="props" color="primary">
            <v-tooltip :text="item.raw.url">
              <template v-slot:activator="{ props: tooltipProps }">
                <p class="text-truncate" v-bind="tooltipProps">
                  {{ item.raw.name }}
                </p>
              </template>
            </v-tooltip>
          </v-chip>
        </template>
      </v-combobox>
    </v-card-text>
  </v-card>
</template>

<style scoped></style>
